import { useEffect, useState } from "react";
import { 
    Box, 
    Card, 
    Button, 
    CircularProgress, 
    Alert,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Typography,
    Chip,
    IconButton,
    Divider,
    Tooltip,
    useTheme
} from "@mui/material";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { useToast } from "OtherReusableFunctionality/ToastContext";
import { Close as CloseIcon } from "@mui/icons-material";
import { Visibility, Edit, Delete } from "@mui/icons-material";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
} from "@mui/material";
import AppPaginationBar from "components/AppPaginationBar";
// ─── CHANGE 1: Import buttonConfig ─────────────────────────────────────────────
import buttonConfig from "config/ConfigAccessButton";
// ─────────────────────────────────────────────────────────────────────────────

const CropHubTable = ({ searchQuery, loading, onRefresh, refreshKey = 0, pageSize: pageSizeProp, onPageSizeChange }) => {
    const theme = useTheme();
    const toast = useToast();
    const [crops, setCrops] = useState([]);
    const [tableLoading, setTableLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        current_page: 1,
        page_size: 10,
        total_items: 0,
        total_pages: 1,
        has_next: false,
        has_previous: false,
        next_page_number: null,
        previous_page_number: null,
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const effectivePageSize = pageSizeProp ?? pageSize;
    const pageSizeOptions = [10, 25, 50, 100];
    const [selectedCrop, setSelectedCrop] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editForm, setEditForm] = useState({
  country_ids: [],
  sequence: '',
  common_name: '',
  scientific_name: '',
  family: '',
  image: '',
  climate_requirement: '',
  temperature_requirement: '',
  rainfall_requirement: '',
  soil_type: '',
  soil_ph_range: '',
  seed_rate_kg_per_ha: '',
  variety_name: '',
  sowing_time: '',
  seed_treatment_details: '',
  sowing_depth: '',
  land_preparation_methods: '',
  fertilizer_nutrient_requirement: '',
  irrigation: '',
  harvesting_time: '',
  average_yield_tons_per_ha: ''
});

const [viewDialogOpen, setViewDialogOpen] = useState(false);
const [viewDetails, setViewDetails] = useState(null);
const [viewLoading, setViewLoading] = useState(false);
const [countries, setCountries] = useState([]);
const [loadingCountries, setLoadingCountries] = useState(false);
const [deletingId, setDeletingId] = useState(null);

    // ─── CHANGE 2: Setup permissions from config ─────────────────────────────
    const cropHubConfig = buttonConfig["Crop hub"] || {};
    const can = (action) => !!cropHubConfig[action];
    // ─────────────────────────────────────────────────────────────────────────

    const fetchCropsData = async (pageParam = 1, pageSizeParam = 10, searchParam = searchQuery) => {
        setTableLoading(true);
        setError(null);
        try {
            const baseUrl = API_ENDPOINTS.GET_crops_guid;
            const normalizedSearch = (searchParam || "").trim();
            const params = new URLSearchParams();
            params.set("page", String(pageParam));
            params.set("page_size", String(pageSizeParam));
            if (normalizedSearch) {
                params.set("search", normalizedSearch);
            }
            const sep = baseUrl.includes('?') ? '&' : '?';
            const url = `${baseUrl}${sep}${params.toString()}`;
            const response = await apiService.get(url);
            
            if (response?.success) {
                setCrops(response.data || []);
                const paginationData = response?.pagination || response?.data?.pagination || {};
                if (Object.keys(paginationData).length > 0) {
                    setPagination({
                        current_page: paginationData.current_page ?? pageParam,
                        page_size: paginationData.page_size ?? pageSizeParam,
                        total_items: paginationData.total_items ?? 0,
                        total_pages: paginationData.total_pages ?? 1,
                        has_next: paginationData.has_next ?? false,
                        has_previous: paginationData.has_previous ?? false,
                        next_page_number: paginationData.next_page_number ?? null,
                        previous_page_number: paginationData.previous_page_number ?? null,
                    });
                    setCurrentPage(paginationData.current_page ?? pageParam);
                    setPageSize(paginationData.page_size ?? pageSizeParam);
                } else {
                    const list = response?.data || [];
                    setPagination(prev => ({ ...prev, total_items: list.length, total_pages: 1, current_page: 1, page_size: pageSizeParam }));
                }
            } else {
                setError("Failed to fetch crops data");
                setCrops([]);
            }
        } catch (error) {
            console.error("Error fetching crops:", error);
            setError(error.message || "Failed to load crops data");
            setCrops([]);
        } finally {
            setTableLoading(false);
        }
    };

    const handlePageChange = (_, value) => {
        setCurrentPage(value);
        fetchCropsData(value, effectivePageSize, searchQuery);
    };

    const handlePageSizeChange = (e) => {
        const newSize = Number(e.target.value);
        setPageSize(newSize);
        setCurrentPage(1);
        fetchCropsData(1, newSize, searchQuery);
        onPageSizeChange?.(e);
    };

    useEffect(() => {
        fetchCropsData(currentPage, effectivePageSize, searchQuery);
    }, [refreshKey, currentPage, effectivePageSize, searchQuery]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

const handleView = async (cropId) => {
    setViewDialogOpen(true);
    setViewLoading(true);
    setViewDetails(null);

    try {
        const userId = localStorage.getItem("user_id");

        if (!userId) {
            console.error("User ID not found in localStorage");
            setViewLoading(false);
            return;
        }

        const response = await apiService.get(
            `${API_ENDPOINTS.GET_CROP_DETAILS_BY_ID}?user_id=${userId}&crop_id=${cropId}`
        );

        if (response?.success) {
            setViewDetails(response.data);
        } else {
            console.warn("Failed to fetch crop details:", response?.message);
            setViewDetails(null);
        }
    } catch (error) {
        console.error("Error fetching crop details:", error);
        setViewDetails(null);
    } finally {
        setViewLoading(false);
    }
};

const fetchCountries = async () => {
  setLoadingCountries(true);
  try {
    const userId = localStorage.getItem("user_id");
    if (!userId) {
      console.error("User ID missing");
      setCountries([]);
      return;
    }

    const endpoint = `${API_ENDPOINTS.GET_COUNTRIES_FOR_CROP_GUIDE}?user_id=${userId}`;
    const response = await apiService.get(endpoint);

    if (response?.success && Array.isArray(response?.data)) {
      setCountries(response.data);
    } else if (Array.isArray(response)) {
      setCountries(response);
    } else {
      setCountries([]);
    }
  } catch (error) {
    console.error("Error fetching countries:", error);
    setCountries([]);
  } finally {
    setLoadingCountries(false);
  }
};

useEffect(() => {
  if (dialogOpen) fetchCountries();
}, [dialogOpen]);

const handleEdit = async (crop) => {
  setTableLoading(true);
  setSelectedCrop(crop);

  try {
    const userId = localStorage.getItem("user_id");
    if (!userId) {
      console.error("User ID not found in localStorage");
      return;
    }

    const response = await apiService.get(
      `${API_ENDPOINTS.GET_CROP_DETAILS_BY_ID}?user_id=${userId}&crop_id=${crop.id}`
    );

  if (response?.success && response.data) {
  const data = response.data;
  setSelectedCrop({ id: data.id });
  setEditForm({
    crop_id: data.id,
    country_ids: data.countries?.map(c => c.id) || [],
    sequence: data.sequence || '',
    common_name: data.common_name || '',
    scientific_name: data.scientific_name || '',
    family: data.family || '',
    image: data.image || '',
    climate_requirement: data.climate_requirement || '',
    temperature_requirement: data.temperature_requirement || '',
    rainfall_requirement: data.rainfall_requirement || '',
    soil_type: data.soil_type || '',
    soil_ph_range: data.soil_ph_range || '',
    seed_rate_kg_per_ha: data.seed_rate_kg_per_ha || '',
    variety_name: data.variety_name || '',
    sowing_time: data.sowing_time || '',
    seed_treatment_details: data.seed_treatment_details || '',
    sowing_depth: data.sowing_depth || '',
    land_preparation_methods: data.land_preparation_methods || '',
    fertilizer_nutrient_requirement: data.fertilizer_nutrient_requirement || '',
    irrigation: data.irrigation || '',
    harvesting_time: data.harvesting_time || '',
    average_yield_tons_per_ha: data.average_yield_tons_per_ha || ''
  });

  setDialogOpen(true);
}
 else {
      console.error("Failed to load crop details for editing");
    }
  } catch (error) {
    console.error("Error loading crop details for edit:", error);
  } finally {
    setTableLoading(false);
  }
};

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedCrop(null);
        setEditForm({});
    };

    const handleInputChange = (field, value) => {
        setEditForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

const handleSubmit = async () => {
  try {
    const formData = new FormData();
    formData.append("id", editForm.crop_id || selectedCrop?.id);
    formData.append("sequence", editForm.sequence || '');
    formData.append("common_name", editForm.common_name || '');
    formData.append("scientific_name", editForm.scientific_name || '');
    formData.append("family", editForm.family || '');
    formData.append("climate_requirement", editForm.climate_requirement || '');
    formData.append("temperature_requirement", editForm.temperature_requirement || '');
    formData.append("rainfall_requirement", editForm.rainfall_requirement || '');
    formData.append("soil_type", editForm.soil_type || '');
    formData.append("soil_ph_range", editForm.soil_ph_range || '');
    formData.append("seed_rate_kg_per_ha", editForm.seed_rate_kg_per_ha || '');
    formData.append("variety_name", editForm.variety_name || '');
    formData.append("sowing_time", editForm.sowing_time || '');
    formData.append("seed_treatment_details", editForm.seed_treatment_details || '');
    formData.append("sowing_depth", editForm.sowing_depth || '');
    formData.append("land_preparation_methods", editForm.land_preparation_methods || '');
    formData.append("fertilizer_nutrient_requirement", editForm.fertilizer_nutrient_requirement || '');
    formData.append("irrigation", editForm.irrigation || '');
    formData.append("harvesting_time", editForm.harvesting_time || '');
    formData.append("average_yield_tons_per_ha", editForm.average_yield_tons_per_ha || '');

    if (Array.isArray(editForm.country_ids)) {
      formData.append("country_ids", editForm.country_ids.join(","));
    }

    if (editForm.image instanceof File) {
      formData.append("image", editForm.image);
    }

    const response = await apiService.put(
      API_ENDPOINTS.UPDATE_CROP_GUIDE,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );

   if (response?.success) {
  const message = "Crop updated successfully!";
  toast.showSuccess(message);
  handleCloseDialog();
  setTimeout(() => {
    fetchCropsData(currentPage, pageSize, searchQuery);
  }, 300);
}
else {
      const errorMessage = response?.message || "Failed to update crop details";
      setError(errorMessage);
      toast.showError(errorMessage);
    }
  } catch (error) {
    console.error("Error updating crop:", error);
    const errorMessage = error.message || "Error updating crop details";
    setError(errorMessage);
    toast.showError(errorMessage);
  }
};

const handleDelete = async (id) => {
  setDeletingId(id);
  const userId = localStorage.getItem("user_id");
  try {
    const response = await apiService.delete(`${API_ENDPOINTS.DELETE_CROP_DETAILS}?crop_id=${id}&user_id=${userId}`);
    if (response?.success) {
      const message = "Crop deleted successfully!";
      toast.showSuccess(message);
      await fetchCropsData(currentPage, pageSize, searchQuery);
    } else {
      const errorMessage = response?.message || "Failed to delete crop";
      toast.showError(errorMessage);
    }
  } catch (error) {
    console.error("Delete failed:", error);
    const errorMessage = "Error deleting crop";
    toast.showError(errorMessage);
  } finally {
    setDeletingId(null);
  }
};

    if (error) {
        return (
            <Box p={2}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button 
                    variant="contained" 
                    onClick={() => fetchCropsData(currentPage, pageSize, searchQuery)}
                    disabled={tableLoading}
                >
                    Retry
                </Button>
            </Box>
        );
    }

    return (
        <Box>
            {(loading || tableLoading) && (
                <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                </Box>
            )}

{!loading && !tableLoading && (
  <Box>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        mb: 2,
        flexWrap: "wrap",
        gap: 2,
      }}
    >
      {/* <FormControl size="small" sx={{ minWidth: 110 }}>
        <InputLabel>Rows</InputLabel>
        <Select value={effectivePageSize} label="Rows" onChange={handlePageSizeChange}>
          {pageSizeOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>
      </FormControl> */}
    </Box>
    <Card
      className="bg-white dark:bg-gray-900"
      sx={{
        padding: "0px 16px 32px 16px",
        overflow: "auto",
        maxHeight: 700,
        scrollbarWidth: "thin",
        scrollbarColor:
          theme.palette.mode === "dark"
            ? "#555 #1e1e1e"
            : "#c1c1c1 #f1f1f1",
        "&::-webkit-scrollbar": {
          height: 8,
          width: 8,
        },
        "&::-webkit-scrollbar-track": {
          background:
            theme.palette.mode === "dark"
              ? "#1e1e1e"
              : "#f1f1f1",
          borderRadius: 8,
        },
        "&::-webkit-scrollbar-thumb": {
          background:
            theme.palette.mode === "dark"
              ? "#555"
              : "#c1c1c1",
          borderRadius: 8,
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background:
            theme.palette.mode === "dark"
              ? "#777"
              : "#a8a8a8",
        },
      }}
    >
      {crops.length === 0 ? (
        <Box textAlign="center" p={3}>
          <Typography variant="h6" className="text-gray-600 dark:text-gray-300">
            No crops found
          </Typography>
        </Box>
      ) : (
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            minWidth: "800px",
          }}
        >
          <thead>
            <tr
              className="bg-gray-100 dark:bg-gray-800"
              style={{ backgroundColor: theme.palette.grey[200] }}
            >
              <th
                style={{
                  textAlign: "left",
                  padding: "12px ",
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  backgroundColor: theme.palette.grey[200],
                }}
                className="border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800"
              >
                Image
              </th>
              <th
                style={{
                  textAlign: "left",
                  padding: "12px",
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  backgroundColor: theme.palette.grey[200],
                }}
                className="border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800"
              >
                Common Name
              </th>
              <th
                style={{
                  textAlign: "left",
                  padding: "12px",
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  backgroundColor: theme.palette.grey[200],
                }}
                className="border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800"
              >
                Scientific Name
              </th>
              <th
                style={{
                  textAlign: "left",
                  padding: "12px",
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  backgroundColor: theme.palette.grey[200],
                }}
                className="border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800"
              >
                Countries
              </th>
              <th
                style={{
                  textAlign: "center",
                  padding: "12px",
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  backgroundColor: theme.palette.grey[200],
                }}
                className="border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800"
              >
                Sequence
              </th>
            <th
            className="
              sticky right-0 z-50
              bg-gray-100 dark:bg-gray-800
              border-gray-300 dark:border-gray-700
              text-center
            "
            style={{
              padding: "12px",
              minWidth: "150px",
              boxShadow: "-4px 0 6px rgba(0,0,0,0.25)",
              position: "sticky",
              top: 0,
              right: 0,
              zIndex: 50,
              backgroundColor: theme.palette.grey[200],
            }}
          >
            Actions
          </th>
            </tr>
          </thead>

          <tbody>
            {crops.map((crop, index) => (
              <tr
                key={crop.id || index}
                className={`${
                  index % 2 === 0
                    ? "bg-white dark:bg-gray-900"
                    : "bg-gray-50 dark:bg-gray-800"
                } hover:bg-gray-100 dark:hover:bg-gray-700 transition`}
              >
                <td className="p-3 border-b border-gray-200 dark:border-gray-700">
                  <Avatar
                    src={crop.image}
                    alt={crop.common_name}
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 1,
                      boxShadow: 1,
                    }}
                    variant="rounded"
                  >
                    {!crop.image && crop.common_name?.charAt(0)}
                  </Avatar>
                </td>

                <td className="p-3 border-b border-gray-200 dark:border-gray-700">
                  <Typography
                    variant="subtitle1"
                    className="font-bold text-gray-900 dark:text-gray-100"
                  >
                    {crop.common_name}
                  </Typography>
                </td>

                <td className="p-3 border-b border-gray-200 dark:border-gray-700">
                  <Typography
                    variant="body2"
                    className="italic text-gray-700 dark:text-gray-300"
                  >
                    {crop.scientific_name}
                  </Typography>
                </td>

                <td className="p-3 border-b border-gray-200 dark:border-gray-700">
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {crop.countries?.map((country, idx) => (
                      <Chip
                        key={country.id || idx}
                        label={country.name}
                        size="small"
                        variant="outlined"
                        className="dark:text-gray-200 dark:border-gray-500"
                        padding="25px"
                      />
                    ))}
                  </Box>
                </td>

                <td className="p-3 border-b border-gray-200 dark:border-gray-700" style={{ textAlign: 'center' }}>
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Chip label={crop.sequence} color="primary" size="small" />
                  </Box>
                </td>

                <td className="p-3 border-b border-gray-200 dark:border-gray-700" style={{ textAlign: 'center', position: 'sticky', right: 0, backgroundColor: theme.palette.background.paper }}>
                  <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                    {/* ─── CHANGE 3: View button — disabled when no view permission ────────── */}
                    <Tooltip title={can('view') ? "View" : "No permission to view"}>
                      <span>
                        <IconButton 
                          color="viewBlue" 
                          onClick={() => can('view') && handleView(crop.id)}
                          disabled={!can('view')}
                        >
                          <Visibility />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {/* ─────────────────────────────────────────────────────────────────────── */}

                    {/* ─── CHANGE 4: Edit button — disabled when no edit permission ────────── */}
                    <Tooltip title={can('edit') ? "Edit" : "No permission to edit"}>
                      <span>
                        <IconButton 
                          color="editGreen" 
                          onClick={() => can('edit') && handleEdit(crop)}
                          disabled={!can('edit')}
                        >
                          <Edit />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {/* ─────────────────────────────────────────────────────────────────────── */}

                    {/* ─── CHANGE 5: Delete button — disabled when no delete permission ────── */}
                    <Tooltip title={can('delete') ? "Delete" : "No permission to delete"}>
                      <span>
                        <IconButton
                          color="error"
                          onClick={() => can('delete') && handleDelete(crop.id)}
                          disabled={deletingId === crop.id || !can('delete')}
                        >
                          {deletingId === crop.id ? (
                            <CircularProgress size={18} />
                          ) : (
                            <Delete />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    {/* ─────────────────────────────────────────────────────────────────────── */}
                  </Box>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
    <AppPaginationBar
      pagination={pagination}
      pageSize={effectivePageSize}
      onPageChange={handlePageChange}
      itemLabel="crops"
    />
  </Box>
)}

            <Dialog
              open={dialogOpen}
              onClose={handleCloseDialog}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle sx={{ fontWeight: 600 }}>
                Edit Crop
              </DialogTitle>

              <DialogContent dividers>
                <Box sx={{ pt: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Basic Information
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Common Name"
                        value={editForm.common_name || ''}
                        onChange={(e) => handleInputChange('common_name', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Scientific Name"
                        value={editForm.scientific_name || ''}
                        onChange={(e) => handleInputChange('scientific_name', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Sequence"
                        value={editForm.sequence || ''}
                        onChange={(e) => handleInputChange('sequence', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Family"
                        value={editForm.family || ''}
                        onChange={(e) => handleInputChange('family', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        Growing Conditions
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Climate Requirement"
                        multiline
                        rows={2}
                        value={editForm.climate_requirement || ''}
                        onChange={(e) => handleInputChange('climate_requirement', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Temperature Requirement"
                        value={editForm.temperature_requirement || ''}
                        onChange={(e) => handleInputChange('temperature_requirement', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Rainfall Requirement"
                        value={editForm.rainfall_requirement || ''}
                        onChange={(e) => handleInputChange('rainfall_requirement', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Soil Type"
                        value={editForm.soil_type || ''}
                        onChange={(e) => handleInputChange('soil_type', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Soil pH Range"
                        value={editForm.soil_ph_range || ''}
                        onChange={(e) => handleInputChange('soil_ph_range', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        Cultivation Details
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Seed Rate / Plantlets Rate"
                        value={editForm.seed_rate_kg_per_ha || ''}
                        onChange={(e) => handleInputChange('seed_rate_kg_per_ha', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Variety Name"
                        value={editForm.variety_name || ''}
                        onChange={(e) => handleInputChange('variety_name', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Sowing Time"
                        value={editForm.sowing_time || ''}
                        onChange={(e) => handleInputChange('sowing_time', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Sowing Depth"
                        value={editForm.sowing_depth || ''}
                        onChange={(e) => handleInputChange('sowing_depth', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Land Preparation Methods"
                        multiline
                        rows={2}
                        value={editForm.land_preparation_methods || ''}
                        onChange={(e) => handleInputChange('land_preparation_methods', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Fertilizer & Nutrient Requirement"
                        multiline
                        rows={2}
                        value={editForm.fertilizer_nutrient_requirement || ''}
                        onChange={(e) => handleInputChange('fertilizer_nutrient_requirement', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Irrigation"
                        multiline
                        rows={2}
                        value={editForm.irrigation || ''}
                        onChange={(e) => handleInputChange('irrigation', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Harvesting Time"
                        value={editForm.harvesting_time || ''}
                        onChange={(e) => handleInputChange('harvesting_time', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Average Yield (tons/ha)"
                        value={editForm.average_yield_tons_per_ha || ''}
                        onChange={(e) => handleInputChange('average_yield_tons_per_ha', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Seed Treatment Details"
                        value={editForm.seed_treatment_details || ''}
                        onChange={(e) => handleInputChange('seed_treatment_details', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        Countries
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      <FormControl fullWidth>
                        <InputLabel>Countries</InputLabel>
                        <Select
                          multiple
                          value={editForm.country_ids || []}
                          onChange={(e) => handleInputChange('country_ids', e.target.value)}
                          input={<OutlinedInput label="Countries" />}
                          renderValue={(selected) => (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                              {selected.map((value) => {
                                const country = countries.find((c) => c.id === value);
                                return (
                                  <Chip
                                    key={value}
                                    label={country?.name || value}
                                    color="primary"
                                    size="small"
                                    variant="outlined"
                                  />
                                );
                              })}
                            </Box>
                          )}
                        >
                          {loadingCountries ? (
                            <MenuItem disabled>Loading...</MenuItem>
                          ) : countries.length > 0 ? (
                            countries.map((country) => (
                              <MenuItem key={country.id} value={country.id}>
                                {country.name}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem disabled>No countries found</MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        Crop Image
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      <Box
                        sx={(theme) => ({
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 2,
                          mt: 1,
                          p: 3,
                          width: '100%',
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? theme.palette.grey[900]
                              : theme.palette.grey[50],
                          borderRadius: 2,
                          border: `1px dashed ${
                            theme.palette.mode === 'dark'
                              ? theme.palette.grey[700]
                              : theme.palette.grey[300]
                          }`,
                        })}
                      >
                        <Avatar
                          src={
                            editForm.image
                              ? typeof editForm.image === 'string'
                                ? editForm.image
                                : URL.createObjectURL(editForm.image)
                              : ''
                          }
                          alt={editForm.common_name}
                          sx={{
                            width: 160,
                            height: 160,
                            borderRadius: 3,
                            boxShadow: 3,
                          }}
                          variant="rounded"
                        >
                          {!editForm.image && editForm.common_name?.charAt(0)}
                        </Avatar>

                        <Button variant="outlined" component="label" size="small" >
                          {editForm.image ? 'Change Image' : 'Upload Image'}
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setEditForm((prev) => ({
                                  ...prev,
                                  image: file,
                                }));
                              }
                            }}
                          />
                        </Button>

                        {typeof editForm.image === 'string' && (
                          <Typography variant="caption" color="textSecondary">
                            Current Image from URL
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </DialogContent>

              <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleCloseDialog}>Cancel</Button>
                {/* ─── CHANGE 6: Save button — disabled when no edit permission ────────── */}
                <Button 
                  onClick={handleSubmit} 
                  variant="contained" 
                  color="primary"
                  disabled={!can('edit')}
                >
                  Save Changes
                </Button>
                {/* ─────────────────────────────────────────────────────────────────────── */}
              </DialogActions>
            </Dialog>

            <Dialog
            open={viewDialogOpen}
            onClose={() => setViewDialogOpen(false)}
            maxWidth="md"
            fullWidth
            >
                <Box sx={{display:"flex",justifyContent:"space-between"}}>
            <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                Crop Details
            </DialogTitle>

                <IconButton
                onClick={() => setViewDialogOpen(false)}
                size="small"
                sx={{ ml: 1 }}
                >
                <CloseIcon />
                </IconButton>
            </Box>
            <DialogContent sx={{ backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : "#fafafa" }}>
                {viewLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                    <CircularProgress />
                </Box>
                ) : viewDetails ? (
                <Box sx={{ mt: 2 }}>
                    <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 3,
                        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : "#fff",
                        p: 3,
                        borderRadius: 2,
                        boxShadow: 1,
                        mb: 3,
                    }}
                    >
                    <Avatar
                        src={viewDetails.image}
                        alt={viewDetails.common_name}
                        variant="rounded"
                        sx={{
                        width: 140,
                        height: 140,
                        borderRadius: 3,
                        boxShadow: 3,
                        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : "#e0e0e0",
                        }}
                    >
                        {!viewDetails.image && viewDetails.common_name?.charAt(0)}
                    </Avatar>

                    <Box textAlign={{ xs: "center", md: "left" }}>
                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                        {viewDetails.common_name}
                        </Typography>
                        <Typography
                        variant="subtitle1"
                        color="text.secondary"
                        fontStyle="italic"
                        gutterBottom
                        >
                        {viewDetails.scientific_name}
                        </Typography>
                        <Chip
                        label={`Sequence: ${viewDetails.sequence}`}
                        color="primary"
                        size="small"
                        sx={{ fontWeight: 600 }}
                        />
                    </Box>
                    </Box>

                    <Card
                    sx={{
                        p: 3,
                        borderRadius: 2,
                        boxShadow: 1,
                        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : "#fff",
                    }}
                    >
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Family
                        </Typography>
                        <Typography variant="body1">{viewDetails.family || "-"}</Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Variety
                        </Typography>
                        <Typography variant="body1">{viewDetails.variety_name || "-"}</Typography>
                        </Grid>

                        <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Climate Requirement
                        </Typography>
                        <Typography variant="body1">{viewDetails.climate_requirement || "-"}</Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Temperature Requirement
                        </Typography>
                        <Typography variant="body1">{viewDetails.temperature_requirement || "-"}</Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Rainfall Requirement
                        </Typography>
                        <Typography variant="body1">{viewDetails.rainfall_requirement || "-"}</Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Soil Type
                        </Typography>
                        <Typography variant="body1">{viewDetails.soil_type || "-"}</Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Soil pH Range
                        </Typography>
                        <Typography variant="body1">{viewDetails.soil_ph_range || "-"}</Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Sowing Time
                        </Typography>
                        <Typography variant="body1">{viewDetails.sowing_time || "-"}</Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Harvesting Time
                        </Typography>
                        <Typography variant="body1">{viewDetails.harvesting_time || "-"}</Typography>
                        </Grid>

                        <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Fertilizer & Nutrient Requirement
                        </Typography>
                        <Typography variant="body1">
                            {viewDetails.fertilizer_nutrient_requirement || "-"}
                        </Typography>
                        </Grid>

                        <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Irrigation
                        </Typography>
                        <Typography variant="body1">{viewDetails.irrigation || "-"}</Typography>
                        </Grid>

                        <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Countries
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                            {viewDetails.countries?.map((country, idx) => (
                            <Chip
                                key={country.id || idx}
                                label={country.name}
                                color="success"
                                variant="outlined"
                            />
                            ))}
                        </Box>
                        </Grid>
                    </Grid>
                    </Card>
                </Box>
                ) : (
                <Typography color="textSecondary" sx={{ mt: 2 }}>
                    No details available.
                </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ 
                p: 2, 
                borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : '#eee'}` 
            }}>
                <Button onClick={() => setViewDialogOpen(false)} variant="outlined">
                Close
                </Button>
            </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CropHubTable;